import React, {
  useState,
  useCallback,
  FC,
  ReactNode,
  memo,
  useRef
} from 'react';
import styled from 'styled-components';
import { Block, Text, Icon } from '@/presentation/components';
import moneyIcon from '@/assets/icons/icon-money.svg';
import calendarIcon from '@/assets/icons/icon-calendar.svg';
import StatusRequest from '@/presentation/modules/procedures/components/Cards/request/Status';

// Props para el componente principal
interface ProcedureToggleCardProps {
  name?: string;
  status?: string;
  statusText?: string;
  amount?: number;
  duration?: string;
  date?: string;
  buttonShowMore?: boolean;
  children?: ReactNode;
}

// Props para ToggleCard interno
interface ToggleCardProps {
  header: ReactNode;
  status?: string;
  statusText?: string;
  body: ReactNode;
  footer?: ReactNode;
  showMoreButton?: boolean;
}

const SeeMoreLessButton = styled.button<{ isOpen: boolean }>`
  display: flex;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #5b36f2;
  background-color: #fff;
  border: none;
  align-items: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #5b36f2;
    font-weight: 700;
  }

  &:focus-visible {
    color: #5b36f2;
    font-weight: 700;
    outline: 1px solid #5b36f2;
    border-radius: 4px;
  }

  &:active {
    color: #5b36f2;
  }
`;

const ToggleCard: FC<ToggleCardProps> = memo(
  ({ header, status, statusText, body, footer, showMoreButton = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    return (
      <Block
        customized
        backgroundColor="#fff"
        borderRadius="8px"
        margin="0 0 32px 0"
      >
        <Block
          customized
          backgroundColor="#fff"
          borderRadius="8px"
          shadow="0px 2px 8px -4px rgba(176, 192, 202, 0.2)"
          overflow="hidden"
          padding="12px 12px 0px 12px"
        >
          <Block
            customized
            padding="12px 0px"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            height="40px"
          >
            {header}
          </Block>
          {status && (
            <Block customized margin="8px 0px 0px 0px">
              <StatusRequest status={status ?? ''} name={statusText ?? ''} />
            </Block>
          )}

          <Block customized padding="8px 0px 8px 0px">
            {body}
          </Block>
        </Block>
        <Block
          customized
          gap="1px"
          backgroundColor="#D5E5F0"
          borderRadius="8px"
        >
          {showMoreButton && (
            <Block
              customized
              padding="0px 12px 12px 12px"
              backgroundColor="#fff"
            >
              <SeeMoreLessButton
                ref={buttonRef}
                type="button"
                isOpen={isOpen}
                onClick={toggle}
              >
                {isOpen ? 'Ver menos' : 'Ver más'}
                <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={24} />
              </SeeMoreLessButton>
            </Block>
          )}

          {/* Contenido desplegable */}
          {isOpen && footer && (
            <Block
              customized
              padding="16px 16px 16px 16px"
              backgroundColor="#fff"
              borderRadius="0px 0px 8px 8px"
            >
              {footer}
            </Block>
          )}
        </Block>
      </Block>
    );
  }
);

export const HeaderInfo = styled.div<{ isTwo?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 0;

  > div {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--text-text02);

    > img {
      margin-right: 4px;
    }

    > p {
      white-space: nowrap;
      margin: 0;
    }

    &:not(:last-child)::after {
      content: '|';
      display: inline-block;
      margin: 0 14px;
    }

    @media (max-width: 360px) {
      &:nth-of-type(2)::after {
        display: none;
      }
    }
  }

  @media (max-width: 360px) {
    max-width:  '360px')};
  }
`;

const InfoBar: FC<{ amount?: number; duration?: string; date?: string }> = ({
  amount,
  duration,
  date
}) => (
  <Block customized flexDirection="column" alignItems="flex-start">
    <HeaderInfo>
      <div>
        <img src={moneyIcon} alt="IconPushpin" />
        <p
          style={{
            width: 'max-content',
            color:
              amount !== undefined && Number(amount) > 0 ? '#161D1F' : undefined
          }}
        >
          {amount !== undefined && Number(amount) > 0 ? (
            <>
              Costo: <b style={{ fontWeight: 900 }}>S/ {amount}.00</b>
            </>
          ) : (
            <>Sin costo</>
          )}
        </p>
      </div>
      {duration && (
        <div>
          <Icon name="clock" size={16} />
          <p style={{ fontSize: 14, width: 'max-content' }}>{duration}</p>
        </div>
      )}
      {date && (
        <div>
          <img className="PR" src={calendarIcon} alt="IconCalendar" />
          <p style={{ color: 'var(--text-text02)' }}>
            Fecha límite: {date?.toLowerCase() || ''}
          </p>
        </div>
      )}
    </HeaderInfo>
  </Block>
);

const ProcedureToggleCard: FC<ProcedureToggleCardProps> = memo(
  ({
    name,
    status,
    statusText,
    amount,
    duration,
    date,
    buttonShowMore = false,
    children
  }) => (
    <ToggleCard
      header={
        <Text fontSize="16px" fontWeight="700" color="#161D1F">
          {name}
        </Text>
      }
      status={status}
      statusText={statusText}
      body={<InfoBar amount={amount} duration={duration} date={date} />}
      footer={children}
      showMoreButton={buttonShowMore}
    />
  )
);

ProcedureToggleCard.displayName = 'ProcedureToggleCard';
ToggleCard.displayName = 'ToggleCard';
InfoBar.displayName = 'InfoBar';

export default ProcedureToggleCard;
